import * as React from "react"
import { Calendar, ExternalLink, ImageIcon } from "lucide-react"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./tabs"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./card"
import { ButtonWidget } from "./button-widget"
import { cn } from "../../lib/utils"

export interface ContentEntry {
  title: string
  description: string
  date: string
  link: string
  linkText?: string
  image?: string
  details?: string
  extras?: string
}

interface TabSection {
  id: string
  label: string
  content: React.ReactNode
}

interface TabbedViewProps {
  entry: ContentEntry
  customTabs?: TabSection[]
  className?: string
  showImage?: boolean
  defaultTab?: string
}

const TabbedView = React.forwardRef<
  React.ElementRef<typeof Tabs>,
  TabbedViewProps
>(({ entry, customTabs, className, showImage = true, defaultTab = "overview", ...props }, ref) => {
  const defaultTabs: TabSection[] = [
    {
      id: "overview",
      label: "Overview",
      content: (
        <Card>
          <CardHeader>
            <CardTitle className="dsw-flex dsw-items-center dsw-gap-2">
              {entry.title}
              <span className="dsw-flex dsw-items-center dsw-gap-1 dsw-text-xs dsw-text-muted-foreground dsw-font-normal">
                <Calendar className="dsw-h-3 dsw-w-3" />
                {entry.date}
              </span>
            </CardTitle>
            <CardDescription>{entry.description}</CardDescription>
          </CardHeader>
          {showImage && (
            <CardContent>
              <div className="dsw-aspect-video dsw-bg-muted dsw-flex dsw-items-center dsw-justify-center dsw-rounded-lg dsw-mb-4">
                {entry.image ? (
                  <img 
                    src={entry.image} 
                    alt={entry.title}
                    className="dsw-w-full dsw-h-full dsw-object-cover dsw-rounded-lg"
                  />
                ) : (
                  <ImageIcon className="dsw-h-16 dsw-w-16 dsw-text-muted-foreground" />
                )}
              </div>
              <ButtonWidget asChild>
                <a href={entry.link} target="_blank" rel="noopener noreferrer" className="dsw-flex dsw-items-center">
                  <ExternalLink className="dsw-h-4 dsw-w-4 dsw-mr-2" />
                  {entry.linkText || "Open Link"}
                </a>
              </ButtonWidget>
            </CardContent>
          )}
        </Card>
      )
    },
    {
      id: "details",
      label: "Details",
      content: (
        <Card>
          <CardHeader>
            <CardTitle>Additional Details</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="dsw-text-sm dsw-text-muted-foreground">
              {entry.details || "No additional details available."}
            </p>
          </CardContent>
        </Card>
      )
    },
    {
      id: "extras",
      label: "Extras",
      content: (
        <Card>
          <CardHeader>
            <CardTitle>Extra Information</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="dsw-text-sm dsw-text-muted-foreground">
              {entry.extras || "No extra information available."}
            </p>
          </CardContent>
        </Card>
      )
    }
  ]

  const tabs = customTabs || defaultTabs

  return (
    <Tabs ref={ref} defaultValue={defaultTab} className={cn("dsw-w-full", className)} {...props}>
      <TabsList className="dsw-grid dsw-w-full dsw-grid-cols-3">
        {tabs.map((tab) => (
          <TabsTrigger key={tab.id} value={tab.id}>
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>
      {tabs.map((tab) => (
        <TabsContent key={tab.id} value={tab.id}>
          {tab.content}
        </TabsContent>
      ))}
    </Tabs>
  )
})
TabbedView.displayName = "TabbedView"

export { TabbedView } 